'use client';

import clsx from 'clsx';
import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import styles from './Input.module.scss';

function inputHasValue(
  value: InputHTMLAttributes<HTMLInputElement>['value'],
  defaultValue: InputHTMLAttributes<HTMLInputElement>['defaultValue']
): boolean {
  if (typeof value === 'string') return value.length > 0;
  if (typeof value === 'number') return Number.isFinite(value);
  if (Array.isArray(value)) return value.length > 0;
  if (typeof defaultValue === 'string') return defaultValue.length > 0;
  if (typeof defaultValue === 'number') return Number.isFinite(defaultValue);
  return false;
}

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Label text for the input */
  label?: ReactNode;
  /** Helper text displayed below the input */
  helperText?: string;
  /** Error message to display */
  error?: string;
  /** Whether the input has an error */
  hasError?: boolean;
  /** Color variant: primary (green) for light backgrounds, secondary (white) for dark backgrounds */
  variant?: 'primary' | 'secondary';
  /** Label/input layout variant */
  layout?: 'default' | 'floating';
  /** Whether the input should take full width */
  fullWidth?: boolean;
  /** Icon to display at the start of the input */
  iconStart?: ReactNode;
  /** Icon to display at the end of the input */
  iconEnd?: ReactNode;
}

/**
 * Input component with bottom-border design, label, error handling, and icon support.
 * Automatically generates accessible IDs for label-input association.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      helperText,
      error,
      hasError,
      variant = 'primary',
      layout = 'default',
      fullWidth = false,
      iconStart,
      iconEnd,
      className,
      id: providedId,
      disabled,
      readOnly,
      required,
      placeholder,
      value,
      defaultValue,
      ...props
    },
    ref
  ) => {
    const resolvedPlaceholder = placeholder && required ? `${placeholder}*` : placeholder;
    const generatedId = useId();
    const id = providedId || generatedId;
    const errorId = `${id}-error`;
    const helperId = `${id}-helper`;
    const isFloatingLayout = layout === 'floating';
    const hasValue = isFloatingLayout && inputHasValue(value, defaultValue);

    const showError = hasError || !!error;
    const describedBy =
      [showError ? errorId : null, helperText ? helperId : null].filter(Boolean).join(' ') ||
      undefined;

    const field = (
      <>
        {label && (
          <label htmlFor={id} className={styles.label}>
            {label}
            {required && (
              <span className={styles.required} aria-hidden="true">
                *
              </span>
            )}
          </label>
        )}
        <div className={styles.inputWrapper}>
          {iconStart && (
            <span className={styles.iconStart} aria-hidden="true">
              {iconStart}
            </span>
          )}
          <input
            ref={ref}
            id={id}
            disabled={disabled}
            readOnly={readOnly}
            required={required}
            aria-invalid={showError}
            aria-describedby={describedBy}
            placeholder={resolvedPlaceholder}
            value={value}
            defaultValue={defaultValue}
            className={clsx(styles.input, {
              [styles.hasIconStart]: !!iconStart,
              [styles.hasIconEnd]: !!iconEnd,
            })}
            {...props}
          />
          {iconEnd && (
            <span className={styles.iconEnd} aria-hidden="true">
              {iconEnd}
            </span>
          )}
        </div>
      </>
    );

    return (
      <div
        className={clsx(
          styles.wrapper,
          {
            [styles.fullWidth]: fullWidth,
            [styles.hasError]: showError,
            [styles.disabled]: disabled || readOnly,
            [styles.secondary]: variant === 'secondary',
            [styles.floatingLayout]: isFloatingLayout,
          },
          className
        )}
        data-has-value={hasValue ? 'true' : undefined}
      >
        {isFloatingLayout ? <div className={styles.floatingField}>{field}</div> : field}
        {(error || helperText) && (
          <div className={styles.messages}>
            {error && (
              <span id={errorId} className={styles.error} role="alert">
                {error}
              </span>
            )}
            {helperText && !error && (
              <span id={helperId} className={styles.helper}>
                {helperText}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
